import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface PositAnalyticsProps {
  positId: string;
}

const PositAnalytics = ({ positId }: PositAnalyticsProps) => {
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const isMobile = useIsMobile();

  // Prevent scroll reset on page change
  useEffect(() => {
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
  }, []);

  const { data: questions, isLoading } = useQuery({
    queryKey: ["posit-questions", positId, page],
    queryFn: async () => {
      // First, get the posit owner's ID
      const { data: posit, error: positError } = await supabase
        .from("posits")
        .select("user_id")
        .eq("id", positId)
        .maybeSingle();

      if (positError) {
        throw positError;
      }

      if (!posit) {
        return { messages: [], totalCount: 0 };
      }


      // Get total count of questions from posit_query source
      const { count, error: countError } = await supabase
        .from("chat_messages")
        .select("*", { count: "exact", head: true })
        .eq("posit_id", positId)
        .eq("role", "user")
        .eq("source", "posit_query")
        .or(`created_by.is.null,created_by.neq.${posit.user_id}`);

      if (countError) {
        throw countError;
      }


      // Get paginated messages with the same filters
      const { data: messages, error } = await supabase
        .from("chat_messages")
        .select(`
          content,
          created_at,
          created_by
        `)
        .eq("posit_id", positId)
        .eq("role", "user")
        .eq("source", "posit_query")
        .or(`created_by.is.null,created_by.neq.${posit.user_id}`)
        .order("created_at", { ascending: false })
        .range((page - 1) * pageSize, page * pageSize - 1);

      if (error) {
        throw error;
      }


      // If we have messages with created_by, fetch the user details
      if (messages && messages.length > 0) {
        const userIds = messages
          .filter(m => m.created_by)
          .map(m => m.created_by);

        if (userIds.length > 0) {
          const { data: users, error: usersError } = await supabase
            .from("users")
            .select("id, first_name, last_name")
            .in("id", userIds);

          if (usersError) throw usersError;

          // Merge user details with messages
          return {
            messages: messages.map(message => ({
              ...message,
              user: users?.find(u => u.id === message.created_by)
            })),
            totalCount: count || 0
          };
        }
      }

      return { messages: messages || [], totalCount: count || 0 };
    },
  });

  const getUserName = (message: any) => {
    if (!message.created_by) return "Anonymous";
    const user = message.user;
    if (!user?.first_name && !user?.last_name) return "Anonymous";
    return `${user.first_name || ''} ${user.last_name || ''}`.trim();
  };

  const totalPages = Math.ceil((questions?.totalCount || 0) / pageSize);
  const startRange = ((page - 1) * pageSize) + 1;
  const endRange = Math.min(page * pageSize, questions?.totalCount || 0);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-[200px]">Question</TableHead>
              <TableHead className="min-w-[120px]">Asked By</TableHead>
              {!isMobile && (
                <TableHead className="min-w-[150px]">Date</TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {questions?.messages.map((message: any) => (
              <TableRow key={message.created_at}>
                <TableCell className="font-medium break-words">
                  {message.content}
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  {getUserName(message)}
                </TableCell>
                {!isMobile && (
                  <TableCell className="whitespace-nowrap">
                    {format(new Date(message.created_at), 'MMM d, yyyy hh:mm a')}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 py-4">
        <div className="text-sm text-muted-foreground order-2 sm:order-1">
          Showing {startRange} to {endRange} of {questions?.totalCount || 0} questions
        </div>
        <div className="flex items-center space-x-2 order-1 sm:order-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-2 rounded-md border hover:bg-muted disabled:opacity-50 flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            {!isMobile && "Previous"}
          </button>
          <span className="mx-4 whitespace-nowrap">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={!questions?.messages || questions.messages.length < pageSize}
            className="px-3 py-2 rounded-md border hover:bg-muted disabled:opacity-50 flex items-center gap-2"
          >
            {!isMobile && "Next"}
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PositAnalytics;
